import { useCallback } from 'react';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  updatePassword as updateFirebasePassword,
  updateEmail as updateFirebaseEmail,
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { useStore } from '../store/useStore';

export const useAuth = () => {
  const { setUser } = useStore();

  const login = useCallback(async (email: string, password: string) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await setUser(userCredential.user);
      return userCredential.user;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }, [setUser]);

  const signup = useCallback(async (email: string, password: string) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await setUser(userCredential.user);
      return userCredential.user;
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    }
  }, [setUser]);

  const logout = useCallback(async () => {
    try {
      await signOut(auth);
      await setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  }, [setUser]);

  const updatePassword = useCallback(async (newPassword: string) => {
    try {
      if (!auth.currentUser) {
        throw new Error('No user is currently logged in');
      }
      await updateFirebasePassword(auth.currentUser, newPassword);
    } catch (error) {
      console.error('Password update error:', error);
      throw error;
    }
  }, []);

  const updateEmail = useCallback(async (newEmail: string) => {
    try {
      if (!auth.currentUser) {
        throw new Error('No user is currently logged in');
      }
      await updateFirebaseEmail(auth.currentUser, newEmail);
    } catch (error) {
      console.error('Email update error:', error);
      throw error;
    }
  }, []);

  return {
    login,
    signup,
    logout,
    updatePassword,
    updateEmail
  };
}; 